import React, { useEffect, useState, useRef } from "react";
import { useAuth, useSignIn } from "@clerk/clerk-react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import BlueButton from "../components/buttons/BlueButton";
import InputField from "../components/input/InputField";
import { toast } from "react-toastify";
import PassValidateView from "../components/password_validation/PassValidateView";

const ForgotPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState(searchParams.get("email") || "");
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [step, setStep] = useState(1);
  const [error, setError] = useState("");
  const [secondFactor, setSecondFactor] = useState(false);
  const [loadingSend, setLoadingSend] = useState(false);
  const [loadingReset, setLoadingReset] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [loadingResend, setLoadingResend] = useState(false);
  const [isStrongPassword, setIsStrongPassword] = useState(false);

  const navigate = useNavigate();
  const { isSignedIn } = useAuth();
  const { isLoaded, signIn, setActive } = useSignIn();

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    if (isSignedIn) {
      navigate("/");
    }
  }, [isSignedIn, navigate]);

  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  if (!isLoaded) return null;

  const sendResetCode = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoadingSend(true);
    if (!email) {
      setLoadingSend(false);
      toast.error("Please enter your email");
      return;
    }
    try {
      await signIn?.create({
        strategy: "reset_password_email_code",
        identifier: email,
      });
      setStep(2);
    } catch (err: any) {
      toast.error(err?.errors?.[0]?.longMessage || "Failed to send reset code");
    } finally {
      setLoadingSend(false);
    }
  };

  const handleOtpChange = (element: HTMLInputElement, index: number) => {
    if (/^\d*$/.test(element.value)) {
      const newOtp = [...otp];
      newOtp[index] = element.value;
      setOtp(newOtp);
      if (element.value && index < 5) {
        inputRefs.current[index + 1]?.focus();
      } else if (!element.value && index > 0) {
        inputRefs.current[index - 1]?.focus();
      }
    }
  };

  const resetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setLoadingReset(true);
    const code = otp.join("");
    if (code.length < 6) {
      setLoadingReset(false);
      toast.error("Please enter the 6-digit code");
      return;
    }
    if (!password || !confirmPassword || !isStrongPassword) {
      setLoadingReset(false);
      toast.error("Please enter and confirm your new password and make sure it is strong");
      return;
    }
    if (password !== confirmPassword) {
      setLoadingReset(false);
      toast.error("Passwords do not match");
      return;
    }

    try {
      const result = await signIn?.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code,
        password,
      });

      if (result?.status === "needs_second_factor") {
        setSecondFactor(true);
        toast.error("2FA is required, but this UI does not handle it.");
      } else if (result?.status === "complete") {
        await setActive({ session: result.createdSessionId });
        navigate("/");
      } else {
        console.log(result);
      }
    } catch (err: any) {
      toast.error(err?.errors?.[0]?.longMessage || "Failed to reset password");
    } finally {
      setLoadingReset(false);
    }
  };

  const handleResendOtp = async () => {
    setLoadingResend(true);
    try {
      await signIn?.create({
        strategy: "reset_password_email_code",
        identifier: email,
      });
      setResendCooldown(120);
      toast.success("OTP resent to your email.");
    } catch (err: any) {
      toast.error(err?.errors?.[0]?.longMessage || "Failed to resend OTP");
    } finally {
      setLoadingResend(false);
    }
  };

  return (
    <div className="min-h-[60vh] flex items-center justify-center py-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <h1 className="text-lg font-semibold text-center mb-2">
          {step === 1 ? "Forgot Password?" : "Reset Password"}
        </h1>
        <p className="text-center text-gray-600 mb-6 text-sm">
          {step === 1
            ? "Enter your email to receive a reset code."
            : "Enter the code sent to your email and set a new password."}
        </p>

        {step === 1 && (
          <form onSubmit={sendResetCode} className="space-y-4">
            <div>
              <InputField
                label=""
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter Email"
                type="email"
              />
            </div>
            <BlueButton
              label="Continue"
              width="100%"
              height="38px"
              loading={loadingSend}
            />
          </form>
        )}

        {step === 2 && (
          <form onSubmit={resetPassword} className="space-y-4 text-sm">
            <div>
              <label className="block font-medium mb-1 text-center text-sm">
                Enter the 6-digit reset code sent to your email
              </label>
              <div className="flex space-x-2 justify-center mt-2 mb-4">
                {otp.map((data, index) => (
                  <input
                    key={index}
                    type="text"
                    maxLength={1}
                    className="w-10 h-10 text-center border rounded text-sm bg-[#F6F6F6] focus:outline-none focus:ring focus:border-blue-100"
                    value={data}
                    onChange={(e) => handleOtpChange(e.target, index)}
                    ref={(el) => (inputRefs.current[index] = el)}
                    autoFocus={index === 0}
                  />
                ))}
              </div>
              <div className="mb-2 flex justify-center">
                {resendCooldown > 0 || loadingResend ? (
                  <span className="text-sm text-gray-400">
                    Resend OTP in {resendCooldown > 0 ? resendCooldown : 0}s
                  </span>
                ) : (
                  <button
                    type="button"
                    className="text-sm text-blue-600 hover:underline focus:outline-none"
                    onClick={handleResendOtp}
                  >
                    Resend OTP
                  </button>
                )}
              </div>
            </div>
            <hr className="my-4" />
            <div>
              <label
                htmlFor="password"
                className="block font-medium mb-1 text-sm"
              >
                New password
              </label>
              <InputField
                label=""
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter New Password"
                type="password"
              />
            </div>

            <div>
              <label
                htmlFor="confirmPassword"
                className="block font-medium mb-1 text-sm"
              >
                Confirm new password
              </label>
              <InputField
                label=""
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Enter Confirm Password"
                type="password"
              />
            </div>
            <BlueButton
              label="Reset Password"
              width="100%"
              height="38px"
              className="text-sm"
              loading={loadingReset}
            />
            <PassValidateView
              password={password}
              confPassword={confirmPassword}
              setIsStrongPassword={setIsStrongPassword}
            />
            {secondFactor && (
              <p className="text-yellow-600 mt-2 text-center text-sm">
                2FA is required, but this UI does not handle it.
              </p>
            )}
          </form>
        )}

        <div className="mt-6 text-center text-sm text-gray-600">
          {step === 1 ? (
            <p className="mt-2 text-sm text-gray-600 text-center">
              Remember your password?{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Sign in
              </button>
            </p>
          ) : (
            <p className="mt-2 text-sm text-gray-600 text-center">
              Back to{" "}
              <button
                type="button"
                onClick={() => navigate("/login")}
                className="font-medium text-blue-600 hover:text-blue-500"
              >
                Sign in
              </button>
            </p>
          )}
          <p className="mt-1 text-sm text-gray-600 text-center">
            Don't have an account?{" "}
            <button
              type="button"
              onClick={() => navigate("/signup")}
              className="font-medium text-blue-600 hover:text-blue-500"
            >
              Sign up
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
